import React, {
  PropsWithChildren,
  useContext,
} from 'react';
import { flowRight } from 'lodash';
import {
  ELSPropsFromModalService,
  ELSWithModalService,
} from '@els/els-component-modal-react';

import withPageLoader from '../../hocs/with-page-loader/withPageLoader.hoc';
import ChatEditModal, { ChatEditModalId } from '../chat-edit-modal/ChatEditModal.component';
import FocusTrap from 'focus-trap-react';
import { ELSIcon } from '@els/els-component-form-field-react';
import { ELSTooltip } from '../els.components';
import { ChatDto } from '../../apis/nhe-ai-chat-service/nhe-ai-chat-service.dtos';
import { deleteChatAction } from '../app/app-state-actions.utilities';
import { AppContext } from '../app/AppProvider.component';
import { getSortedChats } from '../../pages/ai-chat/ai-chat.utilities';

export type ChatActionMenuPropsOnly = PropsWithChildren<{
  chat: ChatDto;
  idPrefix: string;
}>;

export type ChatActionMenuProps = ELSPropsFromModalService
  & ChatActionMenuPropsOnly;

export const ChatActionMenuComponent = (props: ChatActionMenuProps): JSX.Element => {
  const {
    appState,
    setAppState,
    appStateRef
  } = useContext(AppContext);
  return (
    <div className="c-naa-action-menu">
      <ELSTooltip
        id={`chat-item-menu-tooltip-${props.idPrefix}-${props.chat.id}`}
        showClose={false}
        triggerAriaLabel={`Chat options for ${props.chat.title}`}
        dialogAriaLabel="Chat options menu"
        content={(
          <div>
            <div>
              <button type="button"
                className="u-els-anchorize"
                aria-label={`Rename chat: ${props.chat.title}`}
                onClick={() => {
                        props.modalService.openCustomModal({
                          modalId: ChatEditModalId,
                          dialogAriaLabel: 'Edit chat',
                          noFocusTrap: true,
                          modal: (
                            <FocusTrap focusTrapOptions={{
                              preventScroll: true
                            }}>
                              <div>
                                <ChatEditModal chat={props.chat} />
                              </div>
                            </FocusTrap>
                          )
                        })
                      }}
              >
                Rename
              </button>
            </div>
            <div className="u-els-margin-top-1o2">
              <button type="button"
                className="u-els-anchorize"
                aria-label={`Delete chat: ${props.chat.title}`}
                onClick={() => {
                        const modalId = 'ConfirmDeleteChatModal';
                        props.modalService.openConfirmModal({
                          modalId,
                          dialogAriaLabel: 'delete chat',
                          noFocusTrap: true,
                          confirmHandler: () => {
                            deleteChatAction({
                              appState,
                              setAppState,
                              appStateRef,
                              id: props.chat.id
                            }).then(() => {
                              setAppState((prevState) => {
                                const newChats = prevState.chats.filter((chat) => {
                                  return chat.id !== props.chat.id;
                                })
                                return {
                                  ...prevState,
                                  chats: newChats,
                                  activeChatId: prevState.activeChatId === props.chat.id
                                    ? getSortedChats(newChats)[0]?.id || null
                                    : prevState.activeChatId
                                }
                              })
                              props.modalService.closeModal(modalId);
                            });
                          },
                          cancelHandler: () => {
                            props.modalService.closeModal(modalId);
                          },
                          content: (
                            <div>
                              <h4>
                                Are you sure you want to delete this chat?
                              </h4>
                              <div className="o-els-container">
                                Chat name: <strong>{props.chat.title}</strong>
                              </div>
                            </div>
                          )
                        })
                      }}
              >
                Delete
              </button>
            </div>
          </div>

        )}
      >
        <span className="c-naa-action-menu__ellipsis">
          <ELSIcon
            size="1x1o4"
            prefix="gizmo"
            name="ellipsis"
            customClass="u-els-display-block"
          />
        </span>
      </ELSTooltip>
    </div>
  );
};

const enhance = flowRight(
  ELSWithModalService,
  withPageLoader,
);

const ChatActionMenu: React.ComponentType<ChatActionMenuPropsOnly> =
  enhance(ChatActionMenuComponent as React.FC<ChatActionMenuProps>) as React.ComponentType<ChatActionMenuPropsOnly>;

export default ChatActionMenu;
